import { useState } from "react";
import { Criteria, CriteriaType, getDefaultCriteria } from "@/types/criteria";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, GripVertical, Save, Info, ChevronDown, ChevronUp, Star, TrendingUp, Layers } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface CriteriaSettingsProps {
  criteria: Criteria[];
  onSave: (criteria: Criteria[]) => void;
}

const CriteriaSettings = ({ criteria: initialCriteria, onSave }: CriteriaSettingsProps) => {
  const [criteria, setCriteria] = useState<Criteria[]>(() => {
    // Only add missing technical fields - NEVER overwrite user descriptions
    const defaults = getDefaultCriteria();
    return initialCriteria.map(c => {
      const defaultCriterion = defaults.find(d => d.key === c.key);
      return {
        ...c,
        // Keep user's description if it exists, only use default if completely empty/undefined
        description: c.description !== undefined && c.description !== null ? c.description : (defaultCriterion?.description || ""),
        // Add new technical fields only if missing
        criteriaType: c.criteriaType || defaultCriterion?.criteriaType || 'standard',
        weight: c.weight || defaultCriterion?.weight || 1,
        baselinePoints: c.baselinePoints ?? c.defaultPoints,
        minPoints: c.minPoints ?? 0,
        maxPoints: c.maxPoints ?? c.defaultPoints
      };
    });
  });
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newCriteria, setNewCriteria] = useState<{
    label: string;
    defaultPoints: number;
    description: string;
    criteriaType: CriteriaType;
    weight: number;
    baselinePoints: number;
    minPoints: number;
    maxPoints: number;
  }>({ 
    label: "", 
    defaultPoints: 10, 
    description: "",
    criteriaType: 'standard',
    weight: 1,
    baselinePoints: 10,
    minPoints: 0,
    maxPoints: 10
  });
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newCriteria = [...criteria];
    const [removed] = newCriteria.splice(draggedIndex, 1);
    newCriteria.splice(index, 0, removed);

    // Update order values
    const updatedCriteria = newCriteria.map((c, i) => ({ ...c, order: i + 1 }));
    setCriteria(updatedCriteria);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleAddCriteria = () => {
    if (!newCriteria.label.trim()) {
      toast.error("Bitte geben Sie eine Bezeichnung ein");
      return;
    }

    const newItem: Criteria = {
      id: crypto.randomUUID(),
      key: `custom_${Date.now()}`,
      label: newCriteria.label,
      description: newCriteria.description,
      defaultPoints: newCriteria.defaultPoints,
      active: true,
      order: criteria.length + 1,
      criteriaType: newCriteria.criteriaType,
      weight: newCriteria.weight,
      baselinePoints: newCriteria.baselinePoints,
      minPoints: newCriteria.minPoints,
      maxPoints: newCriteria.maxPoints,
    };

    setCriteria([...criteria, newItem]);
    setNewCriteria({ 
      label: "", 
      defaultPoints: 10, 
      description: "",
      criteriaType: 'standard',
      weight: 1,
      baselinePoints: 10,
      minPoints: 0,
      maxPoints: 10
    });
    setIsAddDialogOpen(false);
    toast.success("Kriterium hinzugefügt");
  };

  const handleDeleteCriteria = (id: string) => {
    setCriteria(criteria.filter((c) => c.id !== id));
    toast.success("Kriterium gelöscht");
  };

  const handleToggleActive = (id: string) => {
    setCriteria(
      criteria.map((c) => (c.id === id ? { ...c, active: !c.active } : c))
    );
  };

  const handleUpdateLabel = (id: string, label: string) => {
    setCriteria(criteria.map((c) => (c.id === id ? { ...c, label } : c)));
  };

  const handleUpdateDescription = (id: string, description: string) => {
    setCriteria(criteria.map((c) => (c.id === id ? { ...c, description } : c)));
  };

  const handleUpdatePoints = (id: string, defaultPoints: number) => {
    setCriteria(
      criteria.map((c) => (c.id === id ? { ...c, defaultPoints, baselinePoints: defaultPoints, maxPoints: defaultPoints } : c))
    );
  };

  const handleUpdateCriteriaType = (id: string, criteriaType: CriteriaType) => {
    setCriteria(
      criteria.map((c) => (c.id === id ? { ...c, criteriaType } : c))
    );
  };

  const handleUpdateWeight = (id: string, weight: number) => {
    setCriteria(
      criteria.map((c) => (c.id === id ? { ...c, weight } : c))
    );
  };

  const handleUpdateBaselinePoints = (id: string, baselinePoints: number) => {
    setCriteria(
      criteria.map((c) => (c.id === id ? { ...c, baselinePoints } : c))
    );
  };

  const handleUpdateMinPoints = (id: string, minPoints: number) => {
    setCriteria(
      criteria.map((c) => (c.id === id ? { ...c, minPoints } : c))
    );
  };

  const handleUpdateMaxPoints = (id: string, maxPoints: number) => {
    setCriteria(
      criteria.map((c) => (c.id === id ? { ...c, maxPoints } : c))
    );
  };

  const handleSave = () => {
    // Make sure to save with updated keys for renamed criteria
    const updatedCriteria = criteria.map(c => ({
      ...c,
      // Ensure order is maintained
      order: c.order || criteria.indexOf(c) + 1
    }));
    onSave(updatedCriteria);
    toast.success("Einstellungen gespeichert");
  };

  const getWeightLabel = (weight: number) => {
    switch (weight) {
      case 3: return { label: "Sehr wichtig", color: "bg-red-500/20 text-red-700 border-red-500/30" };
      case 2: return { label: "Wichtig", color: "bg-orange-500/20 text-orange-700 border-orange-500/30" };
      default: return { label: "Normal", color: "bg-gray-500/20 text-gray-600 border-gray-500/30" };
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle>Bewertungskriterien verwalten</CardTitle>
              <CardDescription>
                Bearbeiten, aktivieren/deaktivieren oder löschen Sie Bewertungskriterien. Ziehen Sie Kriterien um die Reihenfolge zu ändern.
              </CardDescription>
            </div>
            <Button onClick={() => setIsAddDialogOpen(true)} className="gap-2 w-full sm:w-auto">
              <Plus className="h-4 w-4" />
              Kriterium hinzufügen
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {criteria.map((criterion, index) => (
            <Collapsible
              key={criterion.id}
              open={expandedId === criterion.id}
              onOpenChange={(open) => setExpandedId(open ? criterion.id : null)}
            >
              <div
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={() => handleDrop(index)}
                onDragEnd={handleDragEnd}
                className={`border rounded-lg bg-card transition-all ${
                  draggedIndex === index ? "opacity-50 scale-95" : ""
                } ${
                  dragOverIndex === index ? "border-primary border-2 bg-primary/5" : ""
                }`}
              >
                <div className="flex items-center gap-2 sm:gap-4 p-3 sm:p-4">
                  <GripVertical className="h-5 w-5 text-muted-foreground cursor-grab active:cursor-grabbing flex-shrink-0" />
                  
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3 sm:gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Bezeichnung</Label>
                      <Input
                        value={criterion.label}
                        onChange={(e) => handleUpdateLabel(criterion.id, e.target.value)}
                        placeholder="Kriterienbezeichnung"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-xs">Max-Punkte</Label>
                      <Input
                        type="number"
                        min="0"
                        value={criterion.defaultPoints}
                        onChange={(e) =>
                          handleUpdatePoints(criterion.id, parseInt(e.target.value) || 0)
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs">Typ</Label>
                      <Select 
                        value={criterion.criteriaType} 
                        onValueChange={(v: CriteriaType) => handleUpdateCriteriaType(criterion.id, v)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-popover">
                          <SelectItem value="standard">
                            <span className="flex items-center gap-1">Standard</span>
                          </SelectItem>
                          <SelectItem value="bonus">
                            <span className="flex items-center gap-1"><TrendingUp className="h-3 w-3" />Bonus</span>
                          </SelectItem>
                          <SelectItem value="hybrid">
                            <span className="flex items-center gap-1"><Layers className="h-3 w-3" />Hybrid</span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs">Gewichtung</Label>
                      <Select 
                        value={String(criterion.weight || 1)} 
                        onValueChange={(v) => handleUpdateWeight(criterion.id, parseInt(v))}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-popover">
                          <SelectItem value="1">Normal</SelectItem>
                          <SelectItem value="2">Wichtig (×2)</SelectItem>
                          <SelectItem value="3">Sehr wichtig (×3)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 sm:gap-2">
                    <Switch
                      checked={criterion.active}
                      onCheckedChange={() => handleToggleActive(criterion.id)}
                    />
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        {expandedId === criterion.id ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </CollapsibleTrigger>
                    <Button
                      variant="destructive"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleDeleteCriteria(criterion.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <CollapsibleContent>
                  <div className="px-3 sm:px-4 pb-3 sm:pb-4 pt-0 border-t">
                    <div className="pt-3 space-y-4">
                      {/* Type-specific settings */}
                      {criterion.criteriaType === 'standard' && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-3 bg-muted/50 rounded-lg">
                          <div className="space-y-2">
                            <Label className="text-xs flex items-center gap-1">
                              <Star className="h-3 w-3 text-primary" />
                              Baseline (100%)
                            </Label>
                            <Input
                              type="number"
                              min="0"
                              value={criterion.baselinePoints ?? criterion.defaultPoints}
                              onChange={(e) => handleUpdateBaselinePoints(criterion.id, parseInt(e.target.value) || 0)}
                            />
                            <p className="text-xs text-muted-foreground">Erwartet laut Arbeitsvertrag</p>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Min-Punkte</Label>
                            <Input
                              type="number"
                              value={criterion.minPoints ?? 0}
                              onChange={(e) => handleUpdateMinPoints(criterion.id, parseInt(e.target.value) || 0)}
                            />
                            <p className="text-xs text-muted-foreground">Niedrigste Bewertung</p>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Max-Punkte (Bonus)</Label>
                            <Input
                              type="number"
                              min="0"
                              value={criterion.maxPoints ?? criterion.defaultPoints}
                              onChange={(e) => handleUpdateMaxPoints(criterion.id, parseInt(e.target.value) || 0)}
                            />
                            <p className="text-xs text-muted-foreground">Über 100% möglich</p>
                          </div>
                        </div>
                      )}

                      {criterion.criteriaType === 'bonus' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
                          <div className="space-y-2">
                            <Label className="text-xs flex items-center gap-1">
                              <TrendingUp className="h-3 w-3 text-primary" />
                              Max Bonus-Punkte
                            </Label>
                            <Input
                              type="number"
                              min="0"
                              value={criterion.maxPoints ?? criterion.defaultPoints}
                              onChange={(e) => handleUpdateMaxPoints(criterion.id, parseInt(e.target.value) || 0)}
                            />
                            <p className="text-xs text-muted-foreground">Startet bei 0, max erreichbar</p>
                          </div>
                          <div className="p-3 bg-background rounded">
                            <p className="text-xs text-muted-foreground">
                              <strong>Bonus-Kriterium:</strong> Der Mitarbeiter startet bei 0 Punkten und kann sich bis zum Maximum hocharbeiten (z.B. Überstunden, Montage, Bereitschaft).
                            </p>
                          </div>
                        </div>
                      )}

                      {criterion.criteriaType === 'hybrid' && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                          <div className="space-y-2">
                            <Label className="text-xs">Min-Punkte</Label>
                            <Input
                              type="number"
                              value={criterion.minPoints ?? 0}
                              onChange={(e) => handleUpdateMinPoints(criterion.id, parseInt(e.target.value) || 0)}
                            />
                            <p className="text-xs text-muted-foreground">Kann negativ sein</p>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs flex items-center gap-1">
                              <Star className="h-3 w-3 text-amber-600" />
                              Baseline (100%)
                            </Label>
                            <Input
                              type="number"
                              value={criterion.baselinePoints ?? criterion.defaultPoints}
                              onChange={(e) => handleUpdateBaselinePoints(criterion.id, parseInt(e.target.value) || 0)}
                            />
                            <p className="text-xs text-muted-foreground">Standardwert = 100%</p>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs flex items-center gap-1">
                              <TrendingUp className="h-3 w-3 text-amber-600" />
                              Max-Punkte
                            </Label>
                            <Input
                              type="number"
                              value={criterion.maxPoints ?? criterion.defaultPoints}
                              onChange={(e) => handleUpdateMaxPoints(criterion.id, parseInt(e.target.value) || 0)}
                            />
                            <p className="text-xs text-muted-foreground">Maximal erreichbar</p>
                          </div>
                          <div className="sm:col-span-3 p-3 bg-background rounded">
                            <p className="text-xs text-muted-foreground">
                              <strong>Hybrid-Kriterium:</strong> Baseline entspricht 100%. Kann unter 0 (negativ) oder über Baseline bis Max (positiv) gehen. 
                              {criterion.baselinePoints && criterion.maxPoints && criterion.baselinePoints > 0 ? (
                                <> Bei {criterion.maxPoints} Punkten = {Math.round((criterion.maxPoints / criterion.baselinePoints) * 100)}%</>
                              ) : null}
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Info className="h-4 w-4 text-muted-foreground" />
                          <Label className="text-xs">Beschreibung / Erklärung</Label>
                        </div>
                        <Textarea
                          value={criterion.description || ""}
                          onChange={(e) => handleUpdateDescription(criterion.id, e.target.value)}
                          placeholder="Beschreiben Sie, was dieses Kriterium bewertet..."
                          rows={3}
                          className="resize-none"
                        />
                        <p className="text-xs text-muted-foreground">
                          Diese Erklärung wird den Mitarbeitern in ihrer Bewertungsübersicht angezeigt.
                        </p>
                      </div>

                      {/* Weight indicator */}
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={getWeightLabel(criterion.weight || 1).color}>
                          {getWeightLabel(criterion.weight || 1).label}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          Gewichtete Max-Punkte: <strong>{(criterion.maxPoints ?? criterion.defaultPoints) * (criterion.weight || 1)}</strong>
                        </span>
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} size="lg" className="gap-2">
          <Save className="h-4 w-4" />
          Änderungen speichern
        </Button>
      </div>

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Neues Kriterium hinzufügen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="label">Bezeichnung</Label>
              <Input
                id="label"
                value={newCriteria.label}
                onChange={(e) =>
                  setNewCriteria({ ...newCriteria, label: e.target.value })
                }
                placeholder="z.B. Teamarbeit"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kriterien-Typ</Label>
                <Select 
                  value={newCriteria.criteriaType} 
                  onValueChange={(v: CriteriaType) => setNewCriteria({ ...newCriteria, criteriaType: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                <SelectContent className="bg-popover">
                    <SelectItem value="standard">Standard (100% erwartet)</SelectItem>
                    <SelectItem value="bonus">Bonus (0 bis Max)</SelectItem>
                    <SelectItem value="hybrid">Hybrid (0 → Baseline=100% → Max)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Gewichtung</Label>
                <Select 
                  value={String(newCriteria.weight)} 
                  onValueChange={(v) => setNewCriteria({ ...newCriteria, weight: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="1">Normal</SelectItem>
                    <SelectItem value="2">Wichtig (×2)</SelectItem>
                    <SelectItem value="3">Sehr wichtig (×3)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {newCriteria.criteriaType === 'standard' && (
              <div className="grid grid-cols-3 gap-4 p-3 bg-muted/50 rounded-lg">
                <div className="space-y-2">
                  <Label className="text-xs">Baseline (100%)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={newCriteria.baselinePoints}
                    onChange={(e) =>
                      setNewCriteria({
                        ...newCriteria,
                        baselinePoints: parseInt(e.target.value) || 0,
                        defaultPoints: parseInt(e.target.value) || 0
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Min-Punkte</Label>
                  <Input
                    type="number"
                    value={newCriteria.minPoints}
                    onChange={(e) =>
                      setNewCriteria({
                        ...newCriteria,
                        minPoints: parseInt(e.target.value) || 0
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Max-Punkte</Label>
                  <Input
                    type="number"
                    min="0"
                    value={newCriteria.maxPoints}
                    onChange={(e) =>
                      setNewCriteria({
                        ...newCriteria,
                        maxPoints: parseInt(e.target.value) || 0
                      })
                    }
                  />
                </div>
              </div>
            )}

            {newCriteria.criteriaType === 'bonus' && (
              <div className="space-y-2 p-3 bg-primary/5 rounded-lg border border-primary/20">
                <Label>Max Bonus-Punkte</Label>
                <Input
                  type="number"
                  min="0"
                  value={newCriteria.maxPoints}
                  onChange={(e) =>
                    setNewCriteria({
                      ...newCriteria,
                      maxPoints: parseInt(e.target.value) || 0,
                      defaultPoints: parseInt(e.target.value) || 0
                    })
                  }
                />
                <p className="text-xs text-muted-foreground">Startet bei 0, erreichbar durch Zusatzleistungen</p>
              </div>
            )}

            {newCriteria.criteriaType === 'hybrid' && (
              <div className="grid grid-cols-3 gap-4 p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                <div className="space-y-2">
                  <Label className="text-xs">Min-Punkte</Label>
                  <Input
                    type="number"
                    value={newCriteria.minPoints}
                    onChange={(e) =>
                      setNewCriteria({
                        ...newCriteria,
                        minPoints: parseInt(e.target.value) || 0
                      })
                    }
                  />
                  <p className="text-xs text-muted-foreground">Kann negativ sein</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs flex items-center gap-1">
                    <Star className="h-3 w-3 text-amber-600" />
                    Baseline (100%)
                  </Label>
                  <Input
                    type="number"
                    value={newCriteria.baselinePoints}
                    onChange={(e) =>
                      setNewCriteria({
                        ...newCriteria,
                        baselinePoints: parseInt(e.target.value) || 0,
                        defaultPoints: parseInt(e.target.value) || 0
                      })
                    }
                  />
                  <p className="text-xs text-muted-foreground">Standardwert = 100%</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs flex items-center gap-1">
                    <TrendingUp className="h-3 w-3 text-amber-600" />
                    Max-Punkte
                  </Label>
                  <Input
                    type="number"
                    value={newCriteria.maxPoints}
                    onChange={(e) =>
                      setNewCriteria({
                        ...newCriteria,
                        maxPoints: parseInt(e.target.value) || 0
                      })
                    }
                  />
                  <p className="text-xs text-muted-foreground">Maximal erreichbar</p>
                </div>
                <div className="col-span-3 p-2 bg-background rounded text-xs text-muted-foreground">
                  <strong>Beispiel:</strong> Min -3, Baseline 7, Max 10 → Bei 7 Punkten = 100%, bei 10 Punkten = 143%
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="description">Beschreibung</Label>
              <Textarea
                id="description"
                value={newCriteria.description}
                onChange={(e) =>
                  setNewCriteria({ ...newCriteria, description: e.target.value })
                }
                placeholder="Beschreiben Sie, was dieses Kriterium bewertet..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleAddCriteria}>Hinzufügen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CriteriaSettings;
